import { FlashList, ListRenderItemInfo } from '@shopify/flash-list';
import React, { useCallback, useLayoutEffect, useMemo } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import Animated, {
  FadeIn,
  FadeOut,
  SlideInLeft,
  SlideInRight,
  SlideOutLeft,
  SlideOutRight,
} from 'react-native-reanimated';

import { ActionSheet, Button, ProductListTile, Text, tokens } from '@fhs/ui';

import {
  useEditStepSelectedEntry,
  useOfferCart,
  useSelectedStep,
} from '../../state/offer-guide-cart-state';

import { BottomButtonContainer } from './bottom-button';
import { SelectedEligibleItem } from './selected-eligible-item';
import { SummaryStepItem } from './summary-step-item';

const StepView = Platform.select({
  native: ({ children, isGoingForward = true, skipEntering = false }) => (
    <Animated.View
      style={{ flex: 1 }}
      entering={skipEntering ? undefined : isGoingForward ? SlideInRight : SlideInLeft}
      exiting={isGoingForward ? SlideOutLeft : SlideOutRight}
    >
      {children}
    </Animated.View>
  ),
  web: ({ children }) => <View style={{ flex: 1 }}>{children}</View>,
}) as any;

const ContentView = Platform.select({
  native: ({ children, style = {} }) => (
    <Animated.View style={style} entering={FadeIn.duration(500)} exiting={FadeOut}>
      {children}
    </Animated.View>
  ),
  web: ({ children, style = {} }) => <View style={style}>{children}</View>,
}) as any;

const numColumns = 2;
const renderItem = ({ item, index }: ListRenderItemInfo<any>) => {
  const isFirstColumn = index % numColumns === 0;
  const isLastColumn = index % numColumns === numColumns - 1;
  return (
    <View
      style={[styles.item, isFirstColumn && styles.firstColumn, isLastColumn && styles.lastColumn]}
    >
      <ProductListTile size="lg" {...item} />
    </View>
  );
};

export function Content({ isFirstRender }: { isFirstRender: boolean }) {
  const {
    currentStepIndex,
    isGoingForward,
    selectedEligibleItem,
    setSelectedEligibleItem,
    goToNextNotDoneStep,
  } = useOfferCart();
  const { editStepSelectedEntry } = useEditStepSelectedEntry();

  const selectedStep = useSelectedStep();

  const hasSelectedEntry = !!selectedStep?.selectedEntry;

  const eligibleItemsFormatted = useMemo(
    () =>
      selectedStep?.eligibleItems?.map(eligibleItem => ({
        id: eligibleItem.id,
        title: eligibleItem.displayName,
        description: eligibleItem.descriptionShort,
        badge: eligibleItem.badge,
        image: {
          asset: {
            uri: eligibleItem.image?.asset?.uri,
            blurHash: eligibleItem.image?.asset?.blurHash,
          },
          altText: eligibleItem.image?.altText,
        },
        isAvailable: eligibleItem.isAvailable,
        onPress: () => setSelectedEligibleItem(eligibleItem),
      })),
    [selectedStep?.eligibleItems, setSelectedEligibleItem]
  );

  useLayoutEffect(() => {
    if (selectedStep?.eligibleItems?.length === 1) {
      // When auto selecting an eligible item when there is only one, it's better to consider it as an edit
      setSelectedEligibleItem(selectedStep.eligibleItems[0], true);
    }
  }, [selectedStep?.eligibleItems, setSelectedEligibleItem]);

  const onPressCustomize = useCallback(() => {
    editStepSelectedEntry(currentStepIndex);
  }, [currentStepIndex, editStepSelectedEntry]);

  return (
    <>
      {!selectedEligibleItem && !hasSelectedEntry && (
        <View style={styles.contentHeader}>
          {selectedStep?.title && <Text.Ui size="xs">{selectedStep?.title}</Text.Ui>}
          {selectedStep?.subtitle && (
            <Text.Heading type="three">{selectedStep?.subtitle}</Text.Heading>
          )}
        </View>
      )}
      <StepView key={currentStepIndex} isGoingForward={isGoingForward} skipEntering={isFirstRender}>
        <View style={{ flex: 1 }}>
          {selectedEligibleItem ? (
            <SelectedEligibleItem selectedEligibleItem={selectedEligibleItem} />
          ) : (
            <ContentView style={styles.content}>
              {hasSelectedEntry && (
                <View style={{ paddingTop: 12 }}>
                  <Text.Ui size="md" weight="semibold">
                    Customize Your Selection
                  </Text.Ui>
                  <SummaryStepItem
                    buttonLabel="Customize"
                    style={styles.summaryStepItem}
                    selectedEntry={selectedStep.selectedEntry}
                    selectedEntryImage={selectedStep.selectedEntryImage}
                    onPressButton={onPressCustomize}
                  />
                  <View style={styles.dividerContainer}>
                    <View style={styles.dividerLine} />
                    <Text.Ui size="md">or</Text.Ui>
                    <View style={styles.dividerLine} />
                  </View>
                  <Text.Ui style={{ paddingTop: 16 }} size="md" weight="semibold">
                    Replace Your Selection:
                  </Text.Ui>
                </View>
              )}
              <FlashList
                showsVerticalScrollIndicator={false}
                data={eligibleItemsFormatted}
                estimatedItemSize={200}
                renderItem={renderItem}
                numColumns={numColumns}
                renderScrollComponent={ActionSheet.ScrollView}
              />
              {hasSelectedEntry && (
                <View style={{ marginHorizontal: -16 }}>
                  <BottomButtonContainer>
                    <Button size="xl" onPress={goToNextNotDoneStep}>
                      <Button.Text>Skip</Button.Text>
                    </Button>
                  </BottomButtonContainer>
                </View>
              )}
            </ContentView>
          )}
        </View>
      </StepView>
    </>
  );
}

const styles = StyleSheet.create({
  contentHeader: {
    alignItems: 'center',
    gap: 4,
  },
  summaryStepItem: {
    borderWidth: 1,
    borderColor: tokens.colors.$blackOpacity10,
    marginTop: 12,
    borderRadius: 8,
  },
  dividerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginTop: 16,
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: tokens.colors.$blackOpacity10,
  },
  content: {
    flex: 1,
    paddingHorizontal: 16,
  },
  item: {
    flex: 1,
    paddingLeft: 6,
    paddingRight: 6,
    paddingTop: 12,
  },
  firstColumn: {
    paddingLeft: 0,
  },
  lastColumn: {
    paddingRight: 0,
  },
  buttonContainer: {
    borderTopWidth: 1,
    borderTopColor: tokens.colors.$blackOpacity10,
  },
  button: {
    margin: 16,
  },
});
